/**
 * Verbindungs- und Sync-Zustand für die Oberfläche (Statusanzeige, Einstellungen).
 */
import { useSyncExternalStore } from 'react';

export interface OfflineState {
  /** Server erreichbar (letzte Anfrage erfolgreich). */
  online: boolean;
  /** Gepufferte Änderungen, die noch zum Server müssen. */
  pending: number;
  /** Abgleich oder Senden läuft. */
  syncing: boolean;
  /** Konflikte und abgelehnte Änderungen, die der DM ansehen sollte. */
  problems: number;
  /** Letzter erfolgreicher Abgleich je Kampagne (ISO-Zeit). */
  lastSync: Record<string, string>;
}

let state: OfflineState = { online: true, pending: 0, syncing: false, problems: 0, lastSync: {} };
const listeners = new Set<() => void>();

export function getOfflineState(): OfflineState {
  return state;
}

export function setOfflineState(patch: Partial<OfflineState>): void {
  const next = { ...state, ...patch };
  if (Object.keys(patch).every((k) => next[k as keyof OfflineState] === state[k as keyof OfflineState])) return;
  state = next;
  for (const fn of listeners) fn();
}

export function subscribeOffline(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useOfflineState(): OfflineState {
  return useSyncExternalStore(subscribeOffline, getOfflineState);
}

// ---------- Erreichbarkeit ----------

const PROBE_MS = 4000;
let probeTimer: number | undefined;
const reconnectHandlers = new Set<() => void>();

/** Wird aufgerufen, sobald der Server nach einer Unterbrechung wieder antwortet. */
export function onReconnect(fn: () => void): () => void {
  reconnectHandlers.add(fn);
  return () => reconnectHandlers.delete(fn);
}

export function reportOnline(): void {
  if (state.online) return;
  window.clearTimeout(probeTimer);
  setOfflineState({ online: true });
  for (const fn of reconnectHandlers) fn();
}

export function reportOffline(): void {
  if (!state.online) return;
  setOfflineState({ online: false });
  scheduleProbe();
}

function scheduleProbe() {
  window.clearTimeout(probeTimer);
  probeTimer = window.setTimeout(() => void probe(), PROBE_MS);
}

async function probe() {
  if (state.online) return;
  try {
    const res = await fetch('/api/health', { cache: 'no-store', credentials: 'same-origin' });
    if (res.ok) return reportOnline();
  } catch {
    /* weiter offline */
  }
  scheduleProbe();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void probe());
  window.addEventListener('offline', () => reportOffline());
}

/** Netzwerkfehler (kein Server, Proxy meldet Backend nicht erreichbar). */
export function isUnreachableStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504;
}
